import { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { geoRobinson } from 'd3-geo-projection';
import { motion, AnimatePresence } from 'framer-motion';
import type { SocketCloudNetworkTransportTypes } from '@aaimplatform/socket-cloud-types';

export interface GeographicNode {
  id: string;
  label: string;
  latitude: number;
  longitude: number;
  type: 'datacenter' | 'edge' | 'cloud' | 'gateway';
  provider?: 'aws' | 'gcp' | 'azure' | 'private';
  region: string;
  status: 'healthy' | 'degraded' | 'unhealthy' | 'offline';
  metrics: {
    cpu: number;
    memory: number;
    network: number;
    connections: number;
  };
  capacity: {
    max: number;
    current: number;
  };
}

export interface GeographicConnection {
  source: string;
  target: string;
  latency: number;
  bandwidth: number;
  status: 'active' | 'degraded' | 'failed';
  type: 'internet' | 'private' | 'vpn' | 'satellite';
  messageRate: number;
}

export type NetworkPartition = SocketCloudNetworkTransportTypes.components['schemas']['NetworkPartition'];

interface GeographicNetworkOverlayProps {
  _nodes: GeographicNode[];
  connections: GeographicConnection[];
  partitions?: NetworkPartition[];
  selectedNode?: string;
  onNodeSelect: (nodeId: string) => void;
  onConnectionSelect: (connection: GeographicConnection) => void;
  showLatencyLines?: boolean;
  showRegionalClusters?: boolean;
  mapStyle?: 'dark' | 'light' | 'satellite';
  width?: number;
  height?: number;
}

// Provider colors
const PROVIDER_COLORS = {
  aws: '#ff9900',
  gcp: '#4285f4',
  azure: '#0078d4',
  private: '#6b7280',
} as const;

// Helper function to properly capitalize provider names
const formatProviderName = (provider: string): string => {
  const providerMap: Record<string, string> = {
    aws: 'AWS',
    gcp: 'GCP',
    azure: 'Azure',
    private: 'Private',
  };
  return providerMap[provider.toLowerCase()] || provider;
};

// Node type colors
const NODE_TYPE_COLORS = {
  datacenter: '#3b82f6',
  edge: '#10b981',
  cloud: '#8b5cf6',
  gateway: '#f59e0b',
} as const;

// Connection type styles
const CONNECTION_STYLES = {
  internet: { color: '#3b82f6', dash: 'none' },
  private: { color: '#10b981', dash: 'none' },
  vpn: { color: '#f59e0b', dash: '5,5' },
  satellite: { color: '#8b5cf6', dash: '10,2,2,2' },
} as const;

/**
 * World map component with network overlay
 */
function WorldMap({
  _nodes,
  connections,
  partitions = [],
  onNodeSelect,
  onConnectionSelect,
  selectedNode,
  showLatencyLines = true,
  showRegionalClusters = true,
  magnificationEnabled = true,
  pinnedNodes,
  setPinnedNodes,
  viewMode,
  width = 1200,
  height = 600,
}: GeographicNetworkOverlayProps & { 
  magnificationEnabled: boolean;
  pinnedNodes: Set<string>;
  setPinnedNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
  viewMode: 'overview' | 'detail' | 'focus';
}) {
  const svgRef = useRef<SVGGElement>(null);
  const containerRef = useRef<SVGSVGElement>(null);
  const [hoveredNode, setHoveredNode] = useState<GeographicNode | null>(null);
  const [hoveredConnection, setHoveredConnection] = useState<GeographicConnection | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);
  const [currentTransform, setCurrentTransform] = useState<d3.ZoomTransform | null>(null);
  const [initialZoomApplied, setInitialZoomApplied] = useState(false);

  // Map projection
  const projection = useMemo(() => {
    // The world.svg uses Robinson projection
    // We need to match its dimensions and positioning exactly
    const mapAspectRatio = 2000 / 857;
    const containerAspectRatio = width / height;
    // Removed unused variables
    
    // Calculate actual dimensions
    let actualWidth, actualHeight;
    if (containerAspectRatio > mapAspectRatio) {
      actualHeight = height;
      actualWidth = height * mapAspectRatio;
    } else {
      actualWidth = width;
      actualHeight = width / mapAspectRatio;
    }
    
    // Robinson projection scale factor
    // The world.svg has viewBox="0 0 2000 857"
    // Robinson projection naturally spans ~1.97x its height
    // We need to match the exact scale of the SVG
    
    // Calculate the actual map dimensions as they appear in the container
    
    // Robinson projection scale calibration
    // The world.svg dimensions are 2000x857
    // Robinson projection natural scale is ~0.15 of width
    // Fine-tuned to match the SVG's projection exactly
    
    // Scale factor calibrated to match world.svg projection
    const scale = actualWidth * 0.165;
    
    // Center the projection - Robinson projection needs slight vertical offset
    const translateX = width / 2;
    const translateY = height / 2 + (actualHeight * 0.05);
    
    return geoRobinson()
      .scale(scale)
      .translate([translateX, translateY])
      .center([0, 0]);
  }, [width, height]);

  // Regional clusters
  const regionalClusters = useMemo(() => {
    if (!showRegionalClusters) {
return [];
}

    const clusters = new Map<string, GeographicNode[]>();
    
    _nodes.forEach(node => {
      if (!clusters.has(node.region)) {
        clusters.set(node.region, []);
      }
      clusters.get(node.region)!.push(node);
    });

    return Array.from(clusters.entries()).map(([region, nodes]) => {
      // Calculate cluster center
      const avgLat = nodes.reduce((sum, n) => sum + n.latitude, 0) / nodes.length;
      const avgLon = nodes.reduce((sum, n) => sum + n.longitude, 0) / nodes.length;
      
      return {
        region,
        _nodes,
        center: { latitude: avgLat, longitude: avgLon },
        healthyCount: nodes.filter(n => n.status === 'healthy').length,
        totalCount: nodes.length,
      };
    });
  }, [_nodes, showRegionalClusters]);

  // Draw map
  useEffect(() => {
    if (!svgRef.current || !containerRef.current || !projection) {
return;
}

    const container = d3.select(containerRef.current);
    const g = d3.select(svgRef.current);
    g.selectAll('*').remove();

    // Add zoom behavior to the container
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 8])
      .on('zoom', (event) => {
        setCurrentTransform(event.transform);
      });

    container.call(zoom);

    // Calculate bounds of all nodes to set initial zoom (only once)
    if (_nodes.length > 0 && !initialZoomApplied) {
      const nodePositions = _nodes.map(node => projection([node.longitude, node.latitude])).filter(p => p !== null) as [number, number][];
      
      if (nodePositions.length > 0) {
        const xExtent = d3.extent(nodePositions, d => d[0]) as [number, number];
        const yExtent = d3.extent(nodePositions, d => d[1]) as [number, number];
        
        const xRange = xExtent[1] - xExtent[0];
        const yRange = yExtent[1] - yExtent[0];
        
        // Add 10% padding
        const padding = 0.1;
        const scaleX = width / (xRange * (1 + padding));
        const scaleY = height / (yRange * (1 + padding));
        const scale = Math.min(scaleX, scaleY, 2); // Cap at 2x zoom
        
        const centerX = (xExtent[0] + xExtent[1]) / 2;
        const centerY = (yExtent[0] + yExtent[1]) / 2;
        
        // Apply initial transform
        const initialTransform = d3.zoomIdentity
          .translate(width / 2, height / 2)
          .scale(scale)
          .translate(-centerX, -centerY);
        
        container.call(zoom.transform, initialTransform);
        setCurrentTransform(initialTransform);
        setInitialZoomApplied(true);
      }
    }

    // Add world map SVG image to the transform group
    // The SVG has viewBox="0 0 2000 857" so we need to scale it appropriately
    const mapAspectRatio = 2000 / 857;
    const containerAspectRatio = width / height;
    let mapWidth, mapHeight, mapX, mapY;
    
    if (containerAspectRatio > mapAspectRatio) {
      // Container is wider than map
      mapHeight = height;
      mapWidth = height * mapAspectRatio;
      mapX = (width - mapWidth) / 2;
      mapY = 0;
    } else {
      // Container is taller than map
      mapWidth = width;
      mapHeight = width / mapAspectRatio;
      mapX = 0;
      mapY = (height - mapHeight) / 2;
    }
    
    g.append('image')
      .attr('xlink:href', '/world.svg')
      .attr('x', mapX)
      .attr('y', mapY)
      .attr('width', mapWidth)
      .attr('height', mapHeight)
      .attr('opacity', 0.25)
      .style('filter', 'invert(1) brightness(0.35) contrast(1.2)')
      .style('pointer-events', 'none');

    // Draw network partitions
    partitions.forEach(partition => {
      if (!partition.endTime) { // Active partition
        const affectedNodePositions = _nodes
          .filter(n => (partition.affectedNodes ?? []).includes(n.id))
          .map(n => projection([n.longitude, n.latitude]));

        if (affectedNodePositions.length > 2) {
          // Create hull around affected nodes
          const hull = d3.polygonHull(affectedNodePositions as [number, number][]);
          if (hull) {
            g.append('path')
              .attr('d', `M${hull.join('L')}Z`)
              .attr('fill', partition.severity === 'critical' ? '#dc2626' :
                           partition.severity === 'high' ? '#ea580c' : '#f59e0b')
              .attr('opacity', 0.2)
              .attr('stroke', partition.severity === 'critical' ? '#dc2626' :
                             partition.severity === 'high' ? '#ea580c' : '#f59e0b')
              .attr('stroke-width', 2)
              .attr('stroke-dasharray', '5,5');
          }
        }
      }
    });

    // Draw regional clusters
    if (showRegionalClusters) {
      regionalClusters.forEach(cluster => {
        const projected = projection([cluster.center.longitude, cluster.center.latitude]);
        if (!projected) {
return;
}
        const [cx, cy] = projected;
        const radius = Math.sqrt(cluster.totalCount) * 10;
        
        g.append('circle')
          .attr('cx', cx)
          .attr('cy', cy)
          .attr('r', radius)
          .attr('fill', 'none')
          .attr('stroke', cluster.healthyCount === cluster.totalCount ? '#22c55e' : '#f59e0b')
          .attr('stroke-width', 2)
          .attr('stroke-opacity', 0.5)
          .attr('stroke-dasharray', '3,3');

        // Cluster label
        g.append('text')
          .attr('x', cx)
          .attr('y', cy - radius - 10)
          .attr('text-anchor', 'middle')
          .attr('fill', '#e5e7eb')
          .attr('font-size', '12px')
          .attr('font-weight', 'bold')
          .text(cluster.region);
      });
    }

    // Draw connections
    if (showLatencyLines) {
      connections.forEach(connection => {
        const sourceNode = _nodes.find(n => n.id === connection.source);
        const targetNode = _nodes.find(n => n.id === connection.target);
        
        if (sourceNode && targetNode) {
          const sourceProjected = projection([sourceNode.longitude, sourceNode.latitude]);
          const targetProjected = projection([targetNode.longitude, targetNode.latitude]);
          if (!sourceProjected || !targetProjected) {
return;
}
          const [x1, y1] = sourceProjected;
          const [x2, y2] = targetProjected;
          
          const style = CONNECTION_STYLES[connection.type];
          const opacity = connection.status === 'active' ? 0.8 : 
                         connection.status === 'degraded' ? 0.5 : 0.3;
          
          // Create curved path for long-distance connections
          const distance = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
          const curvature = Math.min(distance * 0.3, 100);
          
          const midX = (x1 + x2) / 2;
          const midY = (y1 + y2) / 2 - curvature;
          
          const path = `M${x1},${y1} Q${midX},${midY} ${x2},${y2}`;
          
          g.append('path')
            .attr('d', path)
            .attr('stroke', style.color)
            .attr('stroke-width', Math.max(1, connection.bandwidth / 1000))
            .attr('stroke-opacity', opacity)
            .attr('fill', 'none')
            .attr('stroke-dasharray', style.dash)
            .style('cursor', 'pointer')
            .on('click', () => onConnectionSelect(connection))
            .on('mouseover', () => setHoveredConnection(connection))
            .on('mouseout', () => setHoveredConnection(null));

          // Add latency label for high-latency connections
          if (connection.latency > 100) {
            g.append('text')
              .attr('x', midX)
              .attr('y', midY - 5)
              .attr('text-anchor', 'middle')
              .attr('fill', '#fbbf24')
              .attr('font-size', '10px')
              .text(`${Math.round(connection.latency)}ms`);
          }
        }
      });
    }

    // Add mouse tracking for magnification and click handling
    container.on('mousemove', (event) => {
      if (magnificationEnabled) {
        const [mouseX, mouseY] = d3.pointer(event);
        setMousePosition({ x: mouseX, y: mouseY });
      }
    })
    .on('mouseleave', () => {
      setMousePosition(null);
    })
    .on('click', (event) => {
      // Clear all pinned nodes when clicking on background
      if (event.target === event.currentTarget) {
        setPinnedNodes(new Set());
      }
    });

    // Draw nodes with view mode specific sizing and effects
    _nodes.forEach((node, nodeIndex) => {
      const projected = projection([node.longitude, node.latitude]);
      if (!projected) {
return;
} // Skip invalid coordinates
      const [x, y] = projected;
      const isSelected = selectedNode === node.id;
      const hasIssues = node.status !== 'healthy' || (node.capacity.current / node.capacity.max) > 0.8;
      
      // View mode specific node sizing
      let baseNodeSize = Math.max(4, Math.sqrt(node.capacity.current) / 2);
      let viewModeScale = 1;
      
      switch (viewMode) {
        case 'overview':
          viewModeScale = 0.6; // Smaller for overview
          break;
        case 'detail':
          viewModeScale = 1.0; // Normal size
          break;
        case 'focus':
          viewModeScale = hasIssues ? 1.8 : 1.0; // Emphasize problem nodes
          break;
      }
      
      const nodeSize = baseNodeSize * viewModeScale;
      
      // Calculate magnification based on mouse proximity
      let magnification = 1;
      if (mousePosition && magnificationEnabled && currentTransform) {
        // Apply transform to node position for accurate distance calculation
        const transformedX = x * currentTransform.k + currentTransform.x;
        const transformedY = y * currentTransform.k + currentTransform.y;
        const distance = Math.sqrt((mousePosition.x - transformedX) ** 2 + (mousePosition.y - transformedY) ** 2);
        const maxMagnificationDistance = 80; // pixels
        if (distance < maxMagnificationDistance) {
          magnification = 1 + (1 - distance / maxMagnificationDistance) * 1.5; // Up to 2.5x magnification
        }
      }
      
      const finalNodeSize = nodeSize * magnification;
      const labelOpacity = magnification > 1.2 ? 1 : 0.8;
      
      // Create node group for easier manipulation
      const nodeGroup = g.append('g')
        .attr('class', 'node-group')
        .attr('transform', `translate(${x},${y})`);

      // Add focus mode enhancements for problem nodes
      if (viewMode === 'focus' && hasIssues) {
        // Pulsing ring for attention
        nodeGroup.append('circle')
          .attr('r', finalNodeSize + 8)
          .attr('fill', 'none')
          .attr('stroke', node.status === 'unhealthy' ? '#ef4444' : '#f59e0b')
          .attr('stroke-width', 2)
          .attr('opacity', 0.6)
          .style('animation', 'pulse 2s infinite');

        // Tickler line for metrics display
        const ticklerLength = finalNodeSize + 25;
        const ticklerAngle = (nodeIndex % 4) * (Math.PI / 2); // Distribute around node
        const ticklerEndX = Math.cos(ticklerAngle) * ticklerLength;
        const ticklerEndY = Math.sin(ticklerAngle) * ticklerLength;
        
        nodeGroup.append('line')
          .attr('x1', finalNodeSize * Math.cos(ticklerAngle))
          .attr('y1', finalNodeSize * Math.sin(ticklerAngle))
          .attr('x2', ticklerEndX)
          .attr('y2', ticklerEndY)
          .attr('stroke', '#94a3b8')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '2,2');

        // Metrics box at end of tickler line
        const metricsGroup = nodeGroup.append('g')
          .attr('transform', `translate(${ticklerEndX},${ticklerEndY})`);

        metricsGroup.append('rect')
          .attr('x', -25)
          .attr('y', -15)
          .attr('width', 50)
          .attr('height', 30)
          .attr('fill', 'rgba(0, 0, 0, 0.8)')
          .attr('stroke', '#374151')
          .attr('stroke-width', 1)
          .attr('rx', 4);

        metricsGroup.append('text')
          .attr('text-anchor', 'middle')
          .attr('y', -5)
          .attr('fill', '#ef4444')
          .attr('font-size', '8px')
          .attr('font-weight', 'bold')
          .text(node.status === 'unhealthy' ? 'UNHEALTHY' : 'HIGH UTIL');

        metricsGroup.append('text')
          .attr('text-anchor', 'middle')
          .attr('y', 8)
          .attr('fill', '#94a3b8')
          .attr('font-size', '7px')
          .text(`${Math.round((node.capacity.current / node.capacity.max) * 100)}%`);
      }

      // Node background (capacity indicator)
      nodeGroup.append('circle')
        .attr('r', finalNodeSize + 2)
        .attr('fill', '#374151')
        .attr('stroke', PROVIDER_COLORS[node.provider || 'private'])
        .attr('stroke-width', 1)
        .attr('opacity', 0.9);

      // Node utilization fill
      const utilizationRadius = (node.capacity.current / node.capacity.max) * finalNodeSize;
      nodeGroup.append('circle')
        .attr('r', utilizationRadius)
        .attr('fill', NODE_TYPE_COLORS[node.type])
        .attr('opacity', 0.8);

      // Node border
      nodeGroup.append('circle')
        .attr('r', finalNodeSize)
        .attr('fill', 'none')
        .attr('stroke', isSelected ? '#ffffff' : 
                       node.status === 'healthy' ? '#22c55e' :
                       node.status === 'degraded' ? '#f59e0b' :
                       node.status === 'unhealthy' ? '#ef4444' : '#6b7280')
        .attr('stroke-width', isSelected ? 3 : 2)
        .style('cursor', 'pointer')
        .on('click', (event) => {
          event.stopPropagation();
          onNodeSelect(node.id);
          // Toggle pin state
          setPinnedNodes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(node.id)) {
              newSet.delete(node.id);
            } else {
              newSet.add(node.id);
            }
            return newSet;
          });
        })
        .on('mouseover', () => {
          if (!pinnedNodes.has(node.id)) {
            setHoveredNode(node);
          }
        })
        .on('mouseout', () => {
          if (!pinnedNodes.has(node.id)) {
            setHoveredNode(null);
          }
        });

      // Node label with enhanced visibility during magnification
      nodeGroup.append('text')
        .attr('y', finalNodeSize + 15)
        .attr('text-anchor', 'middle')
        .attr('fill', '#e5e7eb')
        .attr('font-size', `${11 * Math.min(magnification, 1.5)}px`)
        .attr('font-weight', magnification > 1.2 ? 'bold' : 'normal')
        .attr('opacity', labelOpacity)
        .text(node.label);

      // Show additional details when magnified
      if (magnification > 1.3) {
        nodeGroup.append('text')
          .attr('y', finalNodeSize + 30)
          .attr('text-anchor', 'middle')
          .attr('fill', '#94a3b8')
          .attr('font-size', '9px')
          .attr('opacity', 0.9)
          .text(`${node.type} | ${((node.capacity.current / node.capacity.max) * 100).toFixed(0)}%`);
      }

      // Provider icon with magnification
      if (node.provider) {
        nodeGroup.append('circle')
          .attr('cx', finalNodeSize - 2)
          .attr('cy', -finalNodeSize + 2)
          .attr('r', 4 * Math.min(magnification, 1.3))
          .attr('fill', PROVIDER_COLORS[node.provider])
          .attr('stroke', '#000000')
          .attr('stroke-width', 1);
        
        // Provider text when magnified
        if (magnification > 1.4) {
          nodeGroup.append('text')
            .attr('x', finalNodeSize + 8)
            .attr('y', -finalNodeSize + 6)
            .attr('fill', PROVIDER_COLORS[node.provider])
            .attr('font-size', '8px')
            .attr('font-weight', 'bold')
            .text(formatProviderName(node.provider || 'private'));
        }
      }

      // Connection count indicator when magnified
      if (magnification > 1.2 && node.metrics.connections > 0) {
        nodeGroup.append('circle')
          .attr('cx', 0)
          .attr('cy', -finalNodeSize - 8)
          .attr('r', 6)
          .attr('fill', '#0ea5e9')
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1)
          .attr('opacity', 0.9);
        
        nodeGroup.append('text')
          .attr('y', -finalNodeSize - 4)
          .attr('text-anchor', 'middle')
          .attr('fill', '#ffffff')
          .attr('font-size', '8px')
          .attr('font-weight', 'bold')
          .text(node.metrics.connections);
      }
    });

  }, [_nodes, connections, partitions, projection, selectedNode, showLatencyLines, showRegionalClusters, regionalClusters, onNodeSelect, onConnectionSelect, magnificationEnabled, mousePosition, viewMode]);

  return (
    <div className="relative" style={{ width, height }}>
      <svg
        ref={containerRef}
        width={width}
        height={height}
        className="rounded-lg"
        style={{ background: 'transparent', overflow: 'visible' }}
      >
        {/* All content will be rendered via D3 */}
        <g 
          ref={svgRef}
          transform={currentTransform ? `translate(${currentTransform.x},${currentTransform.y}) scale(${currentTransform.k})` : ''}
        />
      </svg>
      
      
      {/* Hovered tooltip */}
      <AnimatePresence>
        {hoveredNode && !pinnedNodes.has(hoveredNode.id) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute z-20 bg-black/90 rounded-lg p-3 text-white text-sm pointer-events-none"
            style={{
              left: currentTransform 
                ? ((projection([hoveredNode.longitude, hoveredNode.latitude])?.[0] || 0) * currentTransform.k + currentTransform.x) + 20
                : (projection([hoveredNode.longitude, hoveredNode.latitude])?.[0] || 0) + 20,
              top: currentTransform
                ? ((projection([hoveredNode.longitude, hoveredNode.latitude])?.[1] || 0) * currentTransform.k + currentTransform.y) - 50
                : (projection([hoveredNode.longitude, hoveredNode.latitude])?.[1] || 0) - 50,
            }}
          >
            <div className="font-semibold text-primary-300">{hoveredNode.label}</div>
            <div className="text-xs space-y-1 mt-2">
              <div>Type: <span className="text-yellow-400">{hoveredNode.type}</span></div>
              <div>Provider: <span className="text-secondary-400">{formatProviderName(hoveredNode.provider || 'private')}</span></div>
              <div>Region: <span className="text-primary-400">{hoveredNode.region}</span></div>
              <div>Status: <span className={`${
                hoveredNode.status === 'healthy' ? 'text-status-online' :
                hoveredNode.status === 'degraded' ? 'text-yellow-400' :
                'text-danger-400'
              }`}>{hoveredNode.status}</span></div>
              <div>Utilization: <span className="text-primary-400">
                {((hoveredNode.capacity.current / hoveredNode.capacity.max) * 100).toFixed(1)}%
              </span></div>
              <div>Connections: <span className="text-status-online">{hoveredNode.metrics.connections}</span></div>
            </div>
            <div className="text-xs text-gray-400 mt-2">Click node to pin tooltip</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pinned tooltips */}
      <AnimatePresence>
        {Array.from(pinnedNodes).map(nodeId => {
          const pinnedNode = _nodes.find(n => n.id === nodeId);
          if (!pinnedNode) return null;
          
          return (
            <motion.div
              key={`pinned-${nodeId}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="absolute z-30 bg-black/95 rounded-lg p-3 text-white text-sm cursor-pointer border border-blue-500"
              onClick={(e) => {
                e.stopPropagation();
                setPinnedNodes(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(nodeId);
                  return newSet;
                });
              }}
              style={{
                left: currentTransform 
                  ? ((projection([pinnedNode.longitude, pinnedNode.latitude])?.[0] || 0) * currentTransform.k + currentTransform.x) + 20
                  : (projection([pinnedNode.longitude, pinnedNode.latitude])?.[0] || 0) + 20,
                top: currentTransform
                  ? ((projection([pinnedNode.longitude, pinnedNode.latitude])?.[1] || 0) * currentTransform.k + currentTransform.y) - 50
                  : (projection([pinnedNode.longitude, pinnedNode.latitude])?.[1] || 0) - 50,
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-blue-300">{pinnedNode.label}</div>
                <div className="text-xs text-gray-400">PINNED</div>
              </div>
              <div className="text-xs space-y-1">
                <div>Type: <span className="text-yellow-400">{pinnedNode.type}</span></div>
                <div>Provider: <span className="text-secondary-400">{formatProviderName(pinnedNode.provider || 'private')}</span></div>
                <div>Region: <span className="text-primary-400">{pinnedNode.region}</span></div>
                <div>Status: <span className={`${
                  pinnedNode.status === 'healthy' ? 'text-status-online' :
                  pinnedNode.status === 'degraded' ? 'text-yellow-400' :
                  'text-danger-400'
                }`}>{pinnedNode.status}</span></div>
                <div>Utilization: <span className="text-primary-400">
                  {((pinnedNode.capacity.current / pinnedNode.capacity.max) * 100).toFixed(1)}%
                </span></div>
                <div>Connections: <span className="text-status-online">{pinnedNode.metrics.connections}</span></div>
                <div className="border-t border-gray-600 mt-2 pt-2">
                  <div>CPU: <span className="text-green-400">{pinnedNode.metrics.cpu}%</span></div>
                  <div>Memory: <span className="text-blue-400">{pinnedNode.metrics.memory}%</span></div>
                  <div>Network: <span className="text-purple-400">{pinnedNode.metrics.network}%</span></div>
                </div>
              </div>
              <div className="text-xs text-gray-400 mt-2">Click tooltip to unpin</div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Connection tooltip */}
      <AnimatePresence>
        {hoveredConnection && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute z-20 bg-black/90 rounded-lg p-3 text-white text-sm cursor-pointer"
            onClick={() => setHoveredConnection(null)}
            style={{ left: 20, bottom: 20 }} // Fixed position at bottom left
          >
            <div className="font-semibold">Connection Details</div>
            <div className="text-xs space-y-1 mt-2">
              <div>Type: <span className="text-yellow-400">{hoveredConnection.type}</span></div>
              <div>Latency: <span className="text-primary-400">{Math.round(hoveredConnection.latency)}ms</span></div>
              <div>Bandwidth: <span className="text-status-online">{hoveredConnection.bandwidth.toFixed(1)} MB/s</span></div>
              <div>Message Rate: <span className="text-secondary-400">{hoveredConnection.messageRate}/s</span></div>
              <div>Status: <span className={`${
                hoveredConnection.status === 'active' ? 'text-status-online' :
                hoveredConnection.status === 'degraded' ? 'text-yellow-400' :
                'text-danger-400'
              }`}>{hoveredConnection.status}</span></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Network partition detector and visualizer
 */
function PartitionDetector({
  partitions,
}: {
  partitions: NetworkPartition[];
}) {
  const activePartitions = partitions.filter(p => !p.endTime);
  const recentPartitions = partitions
    .filter(p => p.endTime && Date.now() - new Date(p.endTime).getTime() < 3600000) // Last hour
    .sort((a, b) => (b.endTime ? new Date(b.endTime).getTime() : 0) - (a.endTime ? new Date(a.endTime).getTime() : 0));

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'medium': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Network Partitions</h3>
      
      {/* Active partitions */}
      {activePartitions.length > 0 && (
        <div className="mb-6">
          <h4 className="text-md font-medium text-danger-400 mb-2">Active Partitions</h4>
          <div className="space-y-2">
            {activePartitions.map(partition => (
              <motion.div
                key={partition.id}
                className="bg-red-900/20 rounded-lg p-3"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-white">Partition {partition.id}</span>
                  <span 
                    className="px-2 py-1 rounded text-xs text-white"
                    style={{ backgroundColor: getSeverityColor(partition.severity) }}
                  >
                    {partition.severity.toUpperCase()}
                  </span>
                </div>
                
                <div className="text-sm text-gray-300 space-y-1">
                  <div>Affected Regions: {(partition.affectedRegions ?? []).join(', ')}</div>
                  <div>Affected Nodes: {(partition.affectedNodes ?? []).length}</div>
                  <div>Duration: {partition.startTime ? Math.floor((Date.now() - new Date(partition.startTime).getTime()) / 60000) : 0} minutes</div>
                  {partition.cause && <div>Cause: {partition.cause}</div>}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Recent partitions */}
      {recentPartitions.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-yellow-400 mb-2">Recent Partitions (Resolved)</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {recentPartitions.map(partition => (
              <div
                key={partition.id}
                className="bg-gray-800 rounded-lg p-2"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-white">Partition {partition.id}</span>
                  <span className="text-xs text-gray-400">
                    {partition.endTime ? new Date(partition.endTime).toLocaleTimeString() : ''}
                  </span>
                </div>
                
                <div className="text-xs text-gray-400">
                  Duration: {partition.endTime && partition.startTime ?
                    Math.floor((new Date(partition.endTime).getTime() - new Date(partition.startTime).getTime()) / 60000) :
                    0
                  } minutes
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No partitions */}
      {activePartitions.length === 0 && recentPartitions.length === 0 && (
        <div className="text-center text-status-online py-8">
          <div className="text-2xl mb-2">✓</div>
          <div>No network partitions detected</div>
        </div>
      )}
    </div>
  );
}

/**
 * Data center and cloud provider integration panel
 */
function DataCenterPanel({
  _nodes,
  onNodeSelect: _onNodeSelect,
}: {
  _nodes: GeographicNode[];
  onNodeSelect: (nodeId: string) => void;
}) {
  const providerStats = useMemo(() => {
    const stats = new Map();
    
    _nodes.forEach(node => {
      const provider = node.provider || 'private';
      if (!stats.has(provider)) {
        stats.set(provider, {
          total: 0,
          healthy: 0,
          degraded: 0,
          unhealthy: 0,
          offline: 0,
          totalCapacity: 0,
          usedCapacity: 0,
        });
      }
      
      const stat = stats.get(provider);
      stat.total++;
      stat[node.status]++;
      stat.totalCapacity += node.capacity.max;
      stat.usedCapacity += node.capacity.current;
    });
    
    return Array.from(stats.entries()).map(([provider, stat]) => ({
      provider,
      ...stat,
      utilization: (stat.usedCapacity / stat.totalCapacity) * 100,
    }));
  }, [_nodes]);

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Cloud Providers</h3>
      
      <div className="space-y-4">
        {providerStats.map(({ provider, total, healthy, degraded, unhealthy, offline, utilization }) => (
          <div key={provider} className="bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: PROVIDER_COLORS[provider as keyof typeof PROVIDER_COLORS] || PROVIDER_COLORS.private }}
                />
                <span className="font-medium text-white">{formatProviderName(provider)}</span>
              </div>
              <span className="text-sm text-gray-400">{total} nodes</span>
            </div>
            
            {/* Status breakdown */}
            <div className="flex gap-1 mb-2">
              <div className="flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
                <div className="flex h-full">
                  {healthy > 0 && (
                    <div 
                      className="bg-green-500 h-full"
                      style={{ width: `${(healthy / total) * 100}%` }}
                    />
                  )}
                  {degraded > 0 && (
                    <div 
                      className="bg-yellow-500 h-full"
                      style={{ width: `${(degraded / total) * 100}%` }}
                    />
                  )}
                  {unhealthy > 0 && (
                    <div 
                      className="bg-red-500 h-full"
                      style={{ width: `${(unhealthy / total) * 100}%` }}
                    />
                  )}
                  {offline > 0 && (
                    <div 
                      className="bg-gray-500 h-full"
                      style={{ width: `${(offline / total) * 100}%` }}
                    />
                  )}
                </div>
              </div>
            </div>
            
            {/* Utilization */}
            <div className="text-xs text-gray-400 mb-2">
              Utilization: <span className="text-primary-400">{utilization.toFixed(1)}%</span>
            </div>
            
            {/* Status counts */}
            <div className="flex gap-4 text-xs">
              <span className="text-status-online">{healthy} healthy</span>
              <span className="text-yellow-400">{degraded} degraded</span>
              <span className="text-danger-400">{unhealthy} unhealthy</span>
              <span className="text-gray-400">{offline} offline</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Main geographic network overlay component
 */
export function GeographicNetworkOverlay({
  _nodes,
  connections,
  partitions = [],
  selectedNode,
  onNodeSelect,
  onConnectionSelect,
  showLatencyLines: showLatencyLinesProp = true,
  showRegionalClusters: showRegionalClustersProp = true,
  mapStyle: _mapStyle = 'dark',
  width = 1200,
  height = 600,
}: GeographicNetworkOverlayProps) {
  const [magnificationEnabled, setMagnificationEnabled] = useState(true);
  const [showLatencyLines, setShowLatencyLines] = useState(showLatencyLinesProp);
  const [showRegionalClusters, setShowRegionalClusters] = useState(showRegionalClustersProp);
  const [healthFilter, setHealthFilter] = useState<'all' | 'healthy' | 'issues'>('all');
  const [nodeTypeFilter, setNodeTypeFilter] = useState<'all' | 'datacenter' | 'edge' | 'cloud' | 'gateway'>('all');
  const [viewMode, setViewMode] = useState<'overview' | 'detail' | 'focus'>('overview');
  const [pinnedNodes, setPinnedNodes] = useState<Set<string>>(new Set());
  
  // Filter nodes based on current filters and view mode
  const filteredNodes = _nodes.filter(node => {
    const healthMatch = healthFilter === 'all' || 
                       (healthFilter === 'healthy' && node.status === 'healthy') ||
                       (healthFilter === 'issues' && node.status !== 'healthy');
    
    const typeMatch = nodeTypeFilter === 'all' || node.type === nodeTypeFilter;
    
    // In focus mode, only show nodes with issues or high utilization
    const focusMatch = viewMode !== 'focus' || 
                      node.status !== 'healthy' || 
                      (node.capacity.current / node.capacity.max) > 0.8;
    
    return healthMatch && typeMatch && focusMatch;
  });
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Global Network Overview</h2>
        <div className="flex gap-4 items-center">
          {/* View Mode Tabs */}
          <div className="flex space-x-1 p-1 bg-gray-800 rounded-lg w-fit">
            {[
              { id: 'overview', label: 'Overview', description: 'High-level network view with clusters' },
              { id: 'detail', label: 'Detail', description: 'Individual nodes with magnification' },
              { id: 'focus', label: 'Focus', description: 'Problem investigation mode' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setViewMode(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-200 text-sm font-medium ${
                  viewMode === tab.id
                    ? 'bg-primary-600 text-white shadow-lg'
                    : 'text-console-muted hover:text-console-text hover:bg-secondary-800/50'
                }`}
                title={tab.description}
              >
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Filters */}
          <select
            value={healthFilter}
            onChange={(e) => setHealthFilter(e.target.value as any)}
            className="bg-gray-800 text-white px-2 py-1 rounded text-sm"
          >
            <option value="all">All Health</option>
            <option value="healthy">Healthy Only</option>
            <option value="issues">Issues Only</option>
          </select>
          
          <select
            value={nodeTypeFilter}
            onChange={(e) => setNodeTypeFilter(e.target.value as any)}
            className="bg-gray-800 text-white px-2 py-1 rounded text-sm"
          >
            <option value="all">All Types</option>
            <option value="datacenter">Datacenter</option>
            <option value="edge">Edge</option>
            <option value="cloud">Cloud</option>
            <option value="gateway">Gateway</option>
          </select>
          
          <button 
            onClick={() => setShowLatencyLines(!showLatencyLines)}
            className={`px-3 py-1 text-white rounded text-sm ${showLatencyLines ? 'bg-primary-600' : 'bg-gray-600'}`}
          >
            Latency Lines
          </button>
          <button 
            onClick={() => setShowRegionalClusters(!showRegionalClusters)}
            className={`px-3 py-1 text-white rounded text-sm ${showRegionalClusters ? 'bg-primary-600' : 'bg-gray-600'}`}
          >
            Clusters
          </button>
          <button 
            onClick={() => setMagnificationEnabled(!magnificationEnabled)}
            className={`px-3 py-1 text-white rounded text-sm ${magnificationEnabled ? 'bg-status-online' : 'bg-gray-600'}`}
          >
            Magnification
          </button>
        </div>
      </div>
      
      {/* Stats Bar */}
      <div className="flex items-center gap-6 text-sm text-gray-300">
        <div>Total Nodes: <span className="text-white font-medium">{_nodes.length}</span></div>
        <div>Visible: <span className="text-white font-medium">{filteredNodes.length}</span></div>
        <div>Healthy: <span className="text-green-400 font-medium">{_nodes.filter(n => n.status === 'healthy').length}</span></div>
        <div>Issues: <span className="text-red-400 font-medium">{_nodes.filter(n => n.status !== 'healthy').length}</span></div>
        <div>Active Connections: <span className="text-blue-400 font-medium">{connections.filter(c => c.status === 'active').length}</span></div>
        {pinnedNodes.size > 0 && (
          <div>Pinned: <span className="text-yellow-400 font-medium">{pinnedNodes.size}</span></div>
        )}
      </div>

      {/* Main map and side panels */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Map */}
        <div className="xl:col-span-3 bg-gray-900 rounded-lg p-4 overflow-hidden">
          <WorldMap
            _nodes={filteredNodes}
            connections={connections}
            partitions={partitions}
            selectedNode={selectedNode}
            onNodeSelect={onNodeSelect}
            onConnectionSelect={onConnectionSelect}
            showLatencyLines={showLatencyLines}
            showRegionalClusters={showRegionalClusters}
            magnificationEnabled={magnificationEnabled && viewMode === 'detail'}
            pinnedNodes={pinnedNodes}
            setPinnedNodes={setPinnedNodes}
            viewMode={viewMode}
            width={width}
            height={viewMode === 'focus' ? height + 100 : height}
          />
        </div>

        {/* Side panels */}
        <div className="space-y-6">
          <DataCenterPanel _nodes={_nodes} onNodeSelect={onNodeSelect} />
          <PartitionDetector partitions={partitions} />
        </div>
      </div>
    </div>
  );
}

export default GeographicNetworkOverlay;